/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ROLES } from '../../../data/user.model';
import { connect } from 'react-redux';
import { openModal } from '../../../actions/modals';
import MyPuratosArea from '../ecom/MyPuratosArea';
import { selectLoginUrl, isUserLoggedIn } from '../../../selectors/auth';
import { ProductOrderingModalId } from '../../organisms/modals/ProductOrderingModal';
import { ProductType } from '../../../data/products.model';
import { RoleMatrix } from '../ecom/RoleMatrix';
import { GTMContext } from '../../../analytics/ga4/utils/GTMContext';
import Tooltip from '../../atoms/layout/Tooltip';
import { getHomepage, getLanguageCode } from '../../../selectors/generic';
import { RequestPriceButton } from '../products/RequestPriceButton';
import { getAxios, getEndpointBase } from "../../../api/ECOM/base.api";
import * as querystring from "../../../api/utils/querystring";

class _AddToCartButton extends React.Component {
  static contextType = GTMContext;

  static propTypes = {
    product: PropTypes.shape(ProductType).isRequired,
    loginUrl: PropTypes.string,
    openModal: PropTypes.func.isRequired,
    redirectToLogin: PropTypes.func,
    useHasPrice: PropTypes.bool,
    isDisabled: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = { showLabel: false, packagings: null };
  }

  async componentDidMount() {
    const { product, isNewPdp } = this.props;

    const id = product.id || product.aemId || product.productId;

    if (isNewPdp && product && id) {
      try {
        const packagings = await this.getPackaging({ AEMProductID: id });
        this.setState({ packagings });
      } catch (error) {
        console.error("Error fetching packagings:", error);
      }
    }
  }

  handleMouseHover = () => {
    this.props.label && this.setState(this.toggleHoverState);
  };

  toggleHoverState = (state) => {
    return {
      showLabel: !state.showLabel,
    };
  };

  getPackaging = async ({ AEMProductID }) => {
    const axios = getAxios();
    const countryLanguage = getLanguageCode();
    const apiUrl = getEndpointBase("GetPackaging");

    const queryString = querystring.stringify({
      AEMProductID,
      Language: countryLanguage,
    });

    try {
      const response = await axios.get(`${apiUrl}?${queryString}`);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        throw new Error(`Error: status ${response.status} Data: ${response.data}`);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  onClick = (e) => {
    if (e) {
      e.preventDefault();
    }

    this.props.openModal(ProductOrderingModalId, {
      productId:
        this.props.product.id ||
        this.props.product.aemId ||
        this.props.product.productId,
      hideCartAmountAlert: true,
      type: this.context,
      productCategoryTag: this.props.product.productCategoryTag || ""
    });
  };

  render() {
    const packaging = this.state.packagings;
    const ProductHasPrice = packaging && packaging?.Packaging.some(item => item.HasPrice);
    const ProductIsObsolete = packaging && packaging?.Packaging.every(item => item.IsObsolete);
    const {
      loginUrl,
      product,
      isFinder = false,
      small,
      inverted,
      label,
      padding,
      absolute,
      homepage,
      useHasPrice = true,
      isBlocksale = false
    } = this.props;
    const { showLabel } = this.state;

    if (!product) {
      return null;
    }

    const { materials, packagings } = product;

    const hasPrice = product.hasPrice || ProductHasPrice;
    const isObsolete = product.isObsolete || ProductIsObsolete;
    const isAnyMaterialNumberAvailable = !!(materials && materials[0] && materials[0].materialNumber) || !!(packagings && packagings[0] && packagings[0].code);

    const isDisabled =
      isObsolete ||
      (useHasPrice && !hasPrice) ||
      (isFinder && !isAnyMaterialNumberAvailable) ||
      isBlocksale;

    const availableNoPrice = useHasPrice && !hasPrice && !isObsolete;
    const materialNumber =
      product?.materialNumber ||
      (product?.variationsList &&
        product?.variationsList[0]?.materialNumber) ||
      (product?.packagings && product.packagings[0]?.code);

    const tooltipLabel = homepage ? (
      <div className="p-padding-sm text-center">
        {homepage.accompanyingButtonText && (
          <p
            dangerouslySetInnerHTML={{
              __html: availableNoPrice
                ? homepage.accompanyingButtonText
                : homepage.obsoleteProduct,
            }}
            className={availableNoPrice ? '' : 'p-margin-none'}
          />
        )}
        {availableNoPrice && (
          <RequestPriceButton productId={materialNumber}/>
        )}
      </div>
    ) : null;

    return (
      <MyPuratosArea requireEcom>
        <RoleMatrix>
          <RoleMatrix.Fragment roles={ROLES.connected}>
            <Tooltip
              placement="auto"
              trigger="hover"
              tooltip={tooltipLabel}
              delayHide={125}
              triggerClassName="btn-add-to-cart__wrapper"
              disabled={
                !label ||
                (!useHasPrice && !isObsolete) ||
                (useHasPrice && !isObsolete && hasPrice) ||
                isFinder ||
                !tooltipLabel
              }
            >
              <a
                className={`btn-add-to-cart ${
                  isDisabled ? 'disabled' : ''
                } ${padding ? 'p-margin-left-sm' : ''} ${
                  small ? 'p-absolute' : ''
                }`}
                href="#!"
                onClick={() => this.onClick()}
              >
                                <span
                                  className={`btn-add-to-cart__holder btn-add-to-cart__icon ${
                                    small ? 'small' : ''
                                  }`}
                                />
              </a>
              {showLabel && !isDisabled && (
                <span
                  className={`btn-label ${
                    absolute ? 'btn-label-absolute' : null
                  }`}
                >
                                    {label}
                                </span>
              )}
            </Tooltip>
          </RoleMatrix.Fragment>
          <RoleMatrix.Fragment roles={ROLES.loggedOut}>
            <a
              className={`btn-add-to-cart ${
                isDisabled ? 'disabled' : ''
              } ${padding ? 'p-margin-left-sm' : ''} ${
                small ? 'small' : ''
              }`}
              href={loginUrl}
            >
                            <span
                              className={`btn-add-to-cart__holder btn-add-to-cart__icon ${
                                small ? 'small' : ''
                              }`}
                            />
            </a>
          </RoleMatrix.Fragment>
        </RoleMatrix>
      </MyPuratosArea>
    );
  }
}

const mapStateToProps = (state) => ({
  loggedIn: isUserLoggedIn(state),
  loginUrl: selectLoginUrl(state),
  homepage: getHomepage(state),
});

const mapActionsToProps = {
  openModal,
};

export const AddToCartButton = connect(
  mapStateToProps,
  mapActionsToProps
)(_AddToCartButton);
